// AuthGate — gates the whole app behind a Cognito session (added 2026-06-16).
// Logged out → a login / sign-up screen on the main view. Logged in → the app plus
// a thin "Signed in as … · Sign out" bar. The real security boundary is the API's
// Cognito authorizer; this gate is the SPA-side affordance.

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { COGNITO_USER_POOL_ID, COGNITO_CLIENT_ID, missingConfig } from "../config";
import {
  getIdToken,
  currentUserEmail,
  signIn,
  signUp,
  signOut,
  onAuthExpired,
} from "../auth";

const APP_TITLE = "NGA Ingest";

type Status = "checking" | "authed" | "anon";

export function AuthGate({ children }: { children: ReactNode }) {
  const configured = Boolean(COGNITO_USER_POOL_ID && COGNITO_CLIENT_ID);
  const [status, setStatus] = useState<Status>(configured ? "checking" : "anon");
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!configured) return;
    let active = true;
    void (async () => {
      const token = await getIdToken();
      if (!active) return;
      if (token) {
        setEmail(await currentUserEmail());
        setStatus("authed");
      } else {
        setStatus("anon");
      }
    })();
    // Re-show login if the API later reports the session is no longer valid.
    const off = onAuthExpired(() => {
      if (!active) return;
      setEmail(null);
      setStatus("anon");
    });
    return () => {
      active = false;
      off();
    };
  }, [configured]);

  if (!configured) return <ConfigError />;

  if (status === "checking") {
    return (
      <div className="auth-screen">
        <div className="auth-card auth-card--plain">Checking sign-in…</div>
      </div>
    );
  }

  if (status === "anon") {
    return (
      <LoginScreen
        onAuthed={async () => {
          setEmail(await currentUserEmail());
          setStatus("authed");
        }}
      />
    );
  }

  return (
    <>
      <div className="auth-bar">
        <span className="auth-bar__who">Signed in{email ? ` as ${email}` : ""}</span>
        <button
          type="button"
          className="auth-bar__signout"
          onClick={() => {
            signOut();
            setEmail(null);
            setStatus("anon");
          }}
        >
          Sign out
        </button>
      </div>
      {children}
    </>
  );
}

function LoginScreen({ onAuthed }: { onAuthed: () => void }) {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "signin") await signIn(email.trim(), password);
      else await signUp(email.trim(), password);
      onAuthed();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={submit}>
        <h1 className="auth-card__title">{APP_TITLE}</h1>
        <p className="auth-card__sub">
          {mode === "signin" ? "Sign in to continue" : "Create your account"}
        </p>

        <label className="auth-field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="auth-field">
          <span>Password</span>
          <input
            type="password"
            autoComplete={mode === "signin" ? "current-password" : "new-password"}
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {error && <div className="auth-error">{error}</div>}

        <button className="auth-submit" type="submit" disabled={busy}>
          {busy ? "…" : mode === "signin" ? "Sign in" : "Create account"}
        </button>

        <button
          type="button"
          className="auth-toggle"
          onClick={() => {
            setMode(mode === "signin" ? "signup" : "signin");
            setError(null);
          }}
        >
          {mode === "signin" ? "Need an account? Create one" : "Have an account? Sign in"}
        </button>
      </form>
    </div>
  );
}

function ConfigError() {
  return (
    <div className="auth-screen">
      <div className="auth-card auth-card--plain">
        <h1 className="auth-card__title">Configuration error</h1>
        <p className="auth-card__sub">
          Authentication isn't configured. Missing build-time config:{" "}
          <code>{missingConfig().join(", ")}</code>. Set them in <code>.env</code>{" "}
          (see <code>.env.example</code>) and rebuild.
        </p>
      </div>
    </div>
  );
}
